import { useContext, useEffect, useState } from 'react';
import BaseViewsContext from '../../../../contexts/BaseViewsContext';
import CurrentUserContext from '../../../../contexts/CurrentUserContext';
import ImpersonatorContext from '../../../../contexts/ImpersonatorContext';
import isDefined from '../../../../isDefined';
import useGetDatasetLabel from '../../MetricPicker/MetricPickerPopup/hooks/useGetDatasetLabel';
import useGetDatasetDefinition from '../../../../hooks/useGetDatasetDefinition';

const useDataTypeOptions = ({
  datasetsGreenList,
  isPerformanceDatasetsAllowed,
  alwaysAllowedFields,
}: {
  datasetsGreenList?: string[];
  isPerformanceDatasetsAllowed?: boolean;
  alwaysAllowedFields?: { dataType: string; fields: string[] }[];
}) => {
  const getDatasetLabel = useGetDatasetLabel();
  const [selectedDataType, setSelectedDataType] = useState<
    string | undefined
  >();
  const { baseViews } = useContext(BaseViewsContext);
  const { isFleetOpsStaff } = useContext(CurrentUserContext);
  const { impersonatorId } = useContext(ImpersonatorContext);
  const [dataTypeOptions, setDataTypeOptions] = useState<DropdownOption[]>([]);
  const [allowedDataTypes, setAllowedDataTypes] = useState<string[]>([]);
  const getDatasetDefinition = useGetDatasetDefinition();

  // setAllowedDataTypes
  useEffect(() => {
    const filteredBaseViews = Object.values(baseViews)
      .filter(isDefined)
      .filter((bv) => {
        if (isFleetOpsStaff && !impersonatorId) {
          return true;
        }
        return !bv.isHidden;
      })
      .filter((bv) => {
        if (isPerformanceDatasetsAllowed) {
          return true;
        }
        const ds = getDatasetDefinition(bv.type);
        const isPerformanceDataset = ds ? ds.isPerformance : false;
        return !isPerformanceDataset;
      });

    setAllowedDataTypes(
      filteredBaseViews
        .map((bv) => bv.type)
        .filter((ds) => {
          if (!datasetsGreenList) {
            return true;
          }

          return (
            datasetsGreenList.includes(ds) ||
            (alwaysAllowedFields ?? []).some((a) => a.dataType === ds)
          );
        }),
    );
  }, [
    alwaysAllowedFields,
    baseViews,
    datasetsGreenList,
    getDatasetDefinition,
    impersonatorId,
    isFleetOpsStaff,
    isPerformanceDatasetsAllowed,
  ]);

  // setDataTypeOptions
  useEffect(() => {
    const newOptions = allowedDataTypes.map((dataType) => ({
      label: getDatasetLabel(dataType),
      isSelected: selectedDataType === dataType,
      onSelected: () => {
        setSelectedDataType(dataType);
      },
    }));

    setDataTypeOptions([
      {
        label: 'All Dataset Fields',
        isSelected: selectedDataType === undefined,
        onSelected: () => {
          setSelectedDataType(undefined);
        },
      },
      ...newOptions,
    ]);
  }, [allowedDataTypes, baseViews, getDatasetLabel, selectedDataType]);

  return {
    selectedDataType,
    dataTypeOptions,
    allowedDataTypes,
  };
};

export default useDataTypeOptions;
